import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, X, CalendarDays, Users, ChevronRight } from "lucide-react";
import { Popover, PopoverContent, PopoverAnchor } from "@/components/ui/popover";
import { Drawer, DrawerContent, DrawerTitle, DrawerDescription } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { DatePriceCalendar } from "./DatePriceCalendar";
import { GuestPicker } from "./GuestPicker";
import { useSearch } from "@/lib/search";
import { useLang } from "@/lib/i18n";
import { fmtDate, guestsLabel, nightsBetween, nightsLabel } from "@/lib/format";
import { buildBookingUrl } from "@/lib/booking";

export function useIsMobile(bp = 1024) {
  const [m, setM] = useState(() => (typeof window !== "undefined" ? window.innerWidth < bp : false));
  useEffect(() => {
    const on = () => setM(window.innerWidth < bp);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, [bp]);
  return m;
}

/**
 * True once the element marked `[data-sticky-anchor]` has scrolled above the viewport.
 * `whenMissing` decides the value on pages without an anchor.
 */
export function useAnchorPassed({ whenMissing = false, offset = 72 } = {}) {
  const [passed, setPassed] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => {
    let io;
    let raf;
    let tries = 0;
    const attach = () => {
      const el = document.querySelector("[data-sticky-anchor]");
      if (!el) {
        if (tries++ < 30) { raf = requestAnimationFrame(attach); return; }
        setPassed(whenMissing);
        return;
      }
      if (!("IntersectionObserver" in window)) return;
      io = new IntersectionObserver(([e]) => setPassed(!e.isIntersecting && e.boundingClientRect.top < 0), { rootMargin: `-${offset}px 0px 0px 0px`, threshold: 0 });
      io.observe(el);
    };
    setPassed(false);
    attach();
    return () => { io?.disconnect(); if (raf) cancelAnimationFrame(raf); };
  }, [pathname, whenMissing, offset]);
  return passed;
}

function useGo() {
  const navigate = useNavigate();
  const { search } = useSearch();
  return (extra = {}) => navigate(buildBookingUrl({ ...search, step: 2, ...extra }));
}

/* ------------------------------------------------------------------ mobile drawer */

/** Full-height mobile drawer with calendar + guests. `focus` scrolls to a section on open. */
export const BookingDrawer = ({ open, onOpenChange, focus = "dates" }) => {
  const { search, setSearch } = useSearch();
  const { lang, tx } = useLang();
  const go = useGo();
  const guestsRef = useRef(null);
  const n = nightsBetween(search.ci, search.co);
  const party = search.adults + search.children;
  useEffect(() => {
    if (open && focus === "guests") {
      const t = setTimeout(() => guestsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 250);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [open, focus]);
  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="h-[94dvh] rounded-t-[28px] border-0 bg-background" data-testid="booking-drawer">
        <div className="flex h-full flex-col">
          <div className="flex items-start justify-between gap-4 px-5 pb-4 pt-1">
            <div>
              <DrawerTitle className="text-[1.5rem] font-bold tracking-[-0.02em] text-foreground">{tx("Når vil du kome?", "When are you coming?")}</DrawerTitle>
              <DrawerDescription className="mt-1 text-sm text-muted-foreground">
                {search.ci ? `${fmtDate(search.ci, lang)} – ${search.co ? fmtDate(search.co, lang) : tx("vel utsjekk", "pick check-out")}` : tx("Vel innsjekk, så utsjekk", "Pick check-in, then check-out")}
                {n > 0 && ` · ${nightsLabel(n, lang)}`}
              </DrawerDescription>
            </div>
            <button type="button" onClick={() => onOpenChange(false)} className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary text-foreground" aria-label={tx("Lukk", "Close")} data-testid="booking-drawer-close"><X className="h-4 w-4" /></button>
          </div>
          <div className="flex-1 space-y-8 overflow-y-auto border-t border-border px-5 py-5">
            <DatePriceCalendar ci={search.ci} co={search.co} guests={party} months={2} vertical onChange={(v) => setSearch(v)} testid="booking-drawer-calendar" />
            <div ref={guestsRef} className="scroll-mt-4">
              <p className="text-lg font-bold tracking-tight text-foreground">{tx("Kven kjem?", "Who's coming?")}</p>
              <div className="mt-3 rounded-2xl border border-border bg-card px-4">
                <GuestPicker adults={search.adults} children={search.children} dog={search.dog} onChange={(p) => setSearch(p)} testid="booking-drawer-guests" />
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between gap-4 border-t border-border bg-background px-5 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
            <button type="button" className="text-sm font-semibold text-foreground underline underline-offset-4" onClick={() => setSearch({ ci: "", co: "" })} data-testid="booking-drawer-clear">{tx("Nullstill", "Clear")}</button>
            <Button size="lg" className="px-6" disabled={!search.ci || !search.co} onClick={() => { onOpenChange(false); go(); }} data-testid="booking-drawer-submit">
              <Search className="h-4 w-4" /> {tx("Sjå ledige rom", "See available rooms")}
            </Button>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
};

/* ------------------------------------------------------------------ segments */

/** One field of the search bar. Airbnb rhythm: small bold label on top, value underneath. */
const Seg = ({ label, value, placeholder, onClick, testid, active, compact }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    aria-expanded={active}
    className={`flex h-full min-w-0 flex-1 flex-col justify-center rounded-full text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring ${
      compact ? `px-4 ${active ? "bg-secondary" : "hover:bg-secondary/70"}` : `px-6 ${active ? "bg-secondary" : "hover:bg-secondary/70"}`
    }`}
  >
    {!compact && <span className="block text-xs font-semibold text-foreground">{label}</span>}
    <span className={`block truncate leading-tight ${compact ? "text-sm" : "mt-0.5 text-[0.9375rem]"} ${value ? "font-medium text-foreground" : "text-muted-foreground"} ${compact && value ? "font-semibold" : ""}`}>
      {value || placeholder}
    </span>
  </button>
);

const Divider = ({ hidden, compact }) => <span className={`${compact ? "my-2.5" : "my-4"} w-px shrink-0 bg-border transition-opacity ${hidden ? "opacity-0" : ""}`} aria-hidden />;

/* ------------------------------------------------------------------ mobile hero card */

const MobileHeroCard = ({ testid }) => {
  const { search } = useSearch();
  const { lang, tx } = useLang();
  const go = useGo();
  const [drawer, setDrawer] = useState(false);
  const [focus, setFocus] = useState("dates");
  const party = search.adults + search.children;
  const n = nightsBetween(search.ci, search.co);
  const openWith = (f) => { setFocus(f); setDrawer(true); };
  const submit = () => { if (!search.ci || !search.co) return openWith("dates"); go(); };
  const Row = ({ icon: Icon, label, value, muted, onClick, id }) => (
    <button type="button" onClick={onClick} data-testid={id} className="flex w-full items-center gap-3.5 rounded-[20px] px-3 py-3 text-left transition-colors active:bg-secondary">
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-secondary text-foreground"><Icon className="h-5 w-5" strokeWidth={1.75} /></span>
      <span className="min-w-0 flex-1">
        <span className="block text-xs font-semibold text-foreground">{label}</span>
        <span className={`block truncate text-[0.9375rem] leading-tight ${muted ? "text-muted-foreground" : "font-medium text-foreground"}`}>{value}</span>
      </span>
      <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" />
    </button>
  );
  return (
    <div data-testid={testid} className="rounded-[28px] bg-card p-2 shadow-pill ring-hairline">
      <Row
        icon={CalendarDays}
        label={tx("Datoar", "Dates")}
        value={search.ci && search.co ? `${fmtDate(search.ci, lang, "d. MMM")} – ${fmtDate(search.co, lang, "d. MMM")} · ${nightsLabel(n, lang)}` : tx("Når vil du kome?", "When are you coming?")}
        muted={!(search.ci && search.co)}
        onClick={() => openWith("dates")}
        id={`${testid}-open`}
      />
      <div className="mx-4 h-px bg-border" />
      <Row
        icon={Users}
        label={tx("Gjester", "Guests")}
        value={`${guestsLabel(party, lang)}${search.children > 0 ? ` (${search.adults} + ${search.children})` : ""}${search.dog ? ` · ${tx("hund", "dog")}` : ""}`}
        onClick={() => openWith("guests")}
        id={`${testid}-guests-open`}
      />
      <Button size="xl" className="mt-1.5 w-full rounded-[20px]" onClick={submit} data-testid="booking-bar-submit">
        <Search className="h-[18px] w-[18px]" /> {tx("Sjå ledige rom", "See available rooms")}
      </Button>
      <BookingDrawer open={drawer} onOpenChange={setDrawer} focus={focus} />
    </div>
  );
};

/* ------------------------------------------------------------------ booking bar */

/**
 * Segmented search: check-in | check-out | guests | search.
 * variant: "hero" (large pill; stacked card on mobile) | "compact" (small pill inside the header)
 */
export const BookingBar = ({ variant = "hero", testid = "hero-booking-bar" }) => {
  const { search, setSearch } = useSearch();
  const { lang, tx } = useLang();
  const isMobile = useIsMobile();
  const go = useGo();
  const [calOpen, setCalOpen] = useState(false);
  const [guestOpen, setGuestOpen] = useState(false);
  const party = search.adults + search.children;
  const n = nightsBetween(search.ci, search.co);
  const compact = variant === "compact";
  const anyOpen = calOpen || guestOpen;

  const submit = () => {
    if (!search.ci || !search.co) { setCalOpen(true); return; }
    go();
  };

  const guestText = `${guestsLabel(party, lang)}${search.children > 0 ? ` (${search.adults} + ${search.children})` : ""}${search.dog ? ` · ${tx("hund", "dog")}` : ""}`;

  if (isMobile) return compact ? null : <MobileHeroCard testid={testid} />;

  return (
    <div
      data-testid={testid}
      className={
        compact
          ? "flex h-12 items-stretch rounded-full border border-border bg-card p-1 shadow-float transition-shadow hover:shadow-pill"
          : `flex h-[76px] items-stretch rounded-full bg-card p-2 ring-hairline transition-shadow duration-200 ${anyOpen ? "shadow-pill-hover" : "shadow-pill hover:shadow-pill-hover"}`
      }
    >
      <Popover open={calOpen} onOpenChange={setCalOpen}>
        <PopoverAnchor asChild>
          <div className="flex min-w-0 flex-[2] items-stretch">
            <Seg
              compact={compact}
              label={tx("Innsjekk", "Check-in")}
              value={search.ci && (compact && search.co ? `${fmtDate(search.ci, lang, "d. MMM")} – ${fmtDate(search.co, lang, "d. MMM")}` : fmtDate(search.ci, lang))}
              placeholder={compact ? tx("Legg til datoar", "Add dates") : tx("Legg til dato", "Add date")}
              onClick={() => setCalOpen(true)}
              testid="booking-bar-checkin-input"
              active={calOpen && !search.ci}
            />
            {!compact && (
              <>
                <Divider hidden={calOpen} compact={compact} />
                <Seg
                  compact={compact}
                  label={tx("Utsjekk", "Check-out")}
                  value={search.co && `${fmtDate(search.co, lang)}${n ? ` · ${nightsLabel(n, lang)}` : ""}`}
                  placeholder={tx("Legg til dato", "Add date")}
                  onClick={() => setCalOpen(true)}
                  testid="booking-bar-checkout-input"
                  active={calOpen && !!search.ci && !search.co}
                />
              </>
            )}
          </div>
        </PopoverAnchor>
        <PopoverContent align="start" sideOffset={14} className="w-[min(760px,94vw)] rounded-[28px] border-border bg-card p-6 shadow-pop" data-testid="booking-bar-calendar-popover">
          <div className="mb-4 flex items-end justify-between gap-4">
            <div>
              <p className="text-lg font-bold tracking-tight text-foreground">{search.ci && search.co ? nightsLabel(n, lang) : tx("Vel datoar", "Pick your dates")}</p>
              <p className="mt-0.5 text-sm text-muted-foreground">
                {search.ci ? `${fmtDate(search.ci, lang, "d. MMM yyyy")} – ${search.co ? fmtDate(search.co, lang, "d. MMM yyyy") : tx("vel utsjekk", "pick check-out")}` : tx("Lågaste pris per natt står under kvar dato", "Lowest nightly rate sits under each date")}
              </p>
            </div>
          </div>
          <DatePriceCalendar
            ci={search.ci}
            co={search.co}
            guests={party}
            months={2}
            showLegend={false}
            onChange={(v) => {
              setSearch(v);
              if (v.ci && v.co) setTimeout(() => { setCalOpen(false); setGuestOpen(true); }, 260);
            }}
          />
          <div className="mt-5 flex items-center justify-between border-t border-border pt-4">
            <button type="button" className="text-sm font-semibold text-foreground underline underline-offset-4" onClick={() => setSearch({ ci: "", co: "" })} data-testid="booking-bar-clear-dates">{tx("Nullstill datoar", "Clear dates")}</button>
            <Button onClick={() => setCalOpen(false)} disabled={!search.co} data-testid="booking-bar-dates-done">{tx("Ferdig", "Done")}</Button>
          </div>
        </PopoverContent>
      </Popover>
      <Divider hidden={anyOpen} compact={compact} />
      <Popover open={guestOpen} onOpenChange={setGuestOpen}>
        <PopoverAnchor asChild>
          <div className="flex min-w-0 flex-1 items-stretch">
            <Seg compact={compact} label={tx("Gjester", "Guests")} value={guestText} onClick={() => setGuestOpen(true)} testid="booking-bar-guest-picker" active={guestOpen} />
          </div>
        </PopoverAnchor>
        <PopoverContent align="end" sideOffset={14} className="w-[380px] rounded-[28px] border-border bg-card px-6 py-4 shadow-pop">
          <GuestPicker adults={search.adults} children={search.children} dog={search.dog} onChange={(p) => setSearch(p)} testid="booking-bar-guests" />
          <div className="flex justify-end border-t border-border pt-4">
            <Button onClick={() => setGuestOpen(false)} data-testid="booking-bar-guests-done">{tx("Ferdig", "Done")}</Button>
          </div>
        </PopoverContent>
      </Popover>
      <div className={`flex items-center ${compact ? "" : "pl-1"}`}>
        <Button
          className={compact ? "h-10 w-10 rounded-full p-0" : "h-full rounded-full px-7 text-[0.9375rem]"}
          onClick={submit}
          data-testid="booking-bar-submit"
          aria-label={tx("Søk ledige rom", "Search available rooms")}
        >
          <Search className="h-[18px] w-[18px]" />{!compact && <span>{tx("Sjå ledige rom", "See available rooms")}</span>}
        </Button>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ mobile bottom bar */

/** Mobile bottom bar – hidden while the hero search card is on screen */
export const MobileBookingBar = () => {
  const { search } = useSearch();
  const { lang, tx } = useLang();
  const isMobile = useIsMobile();
  const passed = useAnchorPassed({ whenMissing: true, offset: 64 });
  const [drawer, setDrawer] = useState(false);
  if (!isMobile) return null;
  const n = nightsBetween(search.ci, search.co);
  return (
    <>
      <AnimatePresence>
        {passed && (
          <motion.div
            initial={{ y: 80 }}
            animate={{ y: 0 }}
            exit={{ y: 80 }}
            transition={{ duration: 0.25, ease: [0.2, 0.8, 0.2, 1] }}
            className="no-print fixed inset-x-0 bottom-0 z-40 border-t border-border bg-card/95 px-5 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] backdrop-blur-xl"
            data-testid="mobile-booking-bar"
          >
            <div className="flex items-center justify-between gap-4">
              <button type="button" className="min-w-0 flex-1 text-left" onClick={() => setDrawer(true)} data-testid="mobile-booking-bar-dates">
                <span className="block truncate text-[0.9375rem] font-semibold text-foreground">
                  {search.ci && search.co ? `${fmtDate(search.ci, lang, "d. MMM")} – ${fmtDate(search.co, lang, "d. MMM")}` : tx("Vel datoar", "Choose dates")}
                </span>
                <span className="block truncate text-sm text-muted-foreground underline underline-offset-2">
                  {n > 0 ? `${nightsLabel(n, lang)} · ` : ""}{guestsLabel(search.adults + search.children, lang)}{search.dog ? ` · ${tx("hund", "dog")}` : ""}
                </span>
              </button>
              <Button size="lg" className="px-6" onClick={() => setDrawer(true)} data-testid="mobile-booking-bar-submit">
                {tx("Bestill", "Book")}
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <BookingDrawer open={drawer} onOpenChange={setDrawer} />
    </>
  );
};
